/**
 * Ордер биржевой ноги: что купить, на сколько и по какой цене отказаться.
 *
 * Всё, что здесь считается, отвечает на один вопрос: **до какой цены эта нога
 * ещё стоит того, чтобы её брать**. Ответ нужен потому, что между показом
 * строки и исполнением ордера проходят секунды, а стакан за секунды переезжает.
 * Рыночный ордер без потолка означает «купи по любой цене» — и однажды купит по
 * той, при которой вилки уже нет, а деньги уже потрачены.
 *
 * Потолков два, и оба обязательны.
 *
 * **Цена смерти.** Вилка живёт, пока цена шара ниже `(1 − c)(1 − 1/k)`, где
 * `k` — кэф второй ноги, `c` — доля комиссии биржи. Это не оценка и не
 * настройка: выше этой цены две ноги вместе стоят дороже, чем принесут, при
 * любом исходе. Комиссия берётся по ПОКАЗАННОЙ цене, а не по потолочной: она
 * падает с ростом цены (`rate × (1 − p)`), поэтому такая оценка занижает
 * потолок — ошибается в нашу пользу.
 *
 * **Половина запаса.** Отдавать проскальзыванию весь запас до смерти нельзя:
 * ноль прибыли и голая позиция на второй ноге — это одна и та же цена, взятая с
 * разным настроением. Больше половины запаса не отдаём никогда.
 *
 * Поверх обоих — жёсткие 3% от показанной цены. Если цена уехала сильнее, дело
 * не в проскальзывании: рынок стал другим, и решать по нашей строке уже нельзя.
 * Строку надо перечитать, а не доплатить.
 *
 * Проценты, а не пункты: у дешёвого шара тот же процент — вдвое меньше центов.
 *
 * Здесь нет ни ключей, ни сети, ни SDK — только арифметика над строкой ленты.
 * Исполнение живёт в расширении (`extension/src/poly.ts`), и это разделение не
 * косметическое: решение «покупать ли» обязано быть покрыто тестами без
 * кошелька, без денег и без биржи.
 */

import { Decimal } from 'decimal.js';

import { SPORTS_FEE_RATE, takerCostFraction } from '../lib/calc';
import { MIN_EXCHANGE_ORDER_USD, type RowLeg } from '../lib/row';

/** Какую долю запаса до смерти вилки разрешено отдать проскальзыванию. */
export const KEEP = 0.5;

/** Насколько цена вообще может уехать, прежде чем строку надо перечитать. */
export const MAX_SLIP = 0.03;

/** Шаг цены по умолчанию: цент. Настоящий приходит с рынком (`tick`). */
export const TICK = 0.01;

export interface OrderPlan {
  /** Токен CLOB: тем, что покупаем, ордер называет именно его. */
  tokenId: string;
  /** Сколько долларов кладём. Это же и потолок расхода вместе с комиссией. */
  usd: number;
  /** Цена, которую мы показали человеку, в долларах за шар. */
  price: number;
  /** Выше этой цены не покупаем. Кратна шагу рынка, округлена вниз. */
  maxPrice: number;
  /** Цена, при которой вилки уже нет. Показывается рядом с потолком. */
  dead: number;
  /** Сколько шаров ожидаем по показанной цене. Для подписи, не для ордера. */
  shares: number;
}

export type Verdict = { ok: true; plan: OrderPlan } | { ok: false; why: string };

/**
 * Цена, выше которой вилки нет. `null` — считать не из чего.
 *
 * Кэф второй ноги здесь и есть весь рынок: биржевая нога покупается тем
 * дороже, чем щедрее контора напротив. Ноги «против» в расчёте нет намеренно —
 * см. `planOrder`.
 */
export function deadPrice(
  price: number,
  otherOdds: number,
  feeRate: string = SPORTS_FEE_RATE,
  feeExponent = 1,
): number | null {
  if (!(price > 0) || !(price < 1) || !(otherOdds > 1)) return null;
  const fee = takerCostFraction(new Decimal(price), feeRate, feeExponent);
  const edge = new Decimal(1).minus(new Decimal(1).div(otherOdds));
  return new Decimal(1).minus(fee).times(edge).toNumber();
}

/** Потолок цены к шагу рынка, ВНИЗ: потолок можно занизить, завысить нельзя. */
const toTick = (price: number, tick: number): number => {
  const step = tick > 0 ? tick : TICK;
  return new Decimal(price).div(step).toDecimalPlaces(0, Decimal.ROUND_DOWN).times(step).toNumber();
};

/**
 * Собрать ордер по строке ленты. Отказ — со словами, а не пустым `null`.
 *
 * Отказ здесь дешевле любой попытки: биржевая нога берётся ПЕРВОЙ (`next/order.ts`),
 * и её отказ не оставляет голой позиции. Поэтому каждое сомнение — отказ.
 */
export function planOrder(leg: RowLeg, other: RowLeg): Verdict {
  if (leg.unit !== 'share') return { ok: false, why: 'нога не биржевая, ордера тут нет' };
  // Нога «против» на бирже — это покупка ПРОТИВОПОЛОЖНОГО исхода, то есть
  // другого токена с другой ценой. Собрать её из этих чисел нельзя, а собрать
  // «примерно» значило бы купить не то, что показано.
  if (leg.lay) return { ok: false, why: 'нога «против»: на бирже это другой токен' };
  if (leg.asset === null) return { ok: false, why: 'токен исхода не приехал, покупать нечего' };

  const price = leg.cents === null ? null : leg.cents / 100;
  if (price === null || !(price > 0) || !(price < 1)) {
    return { ok: false, why: 'цены шара нет, покупать не по чему' };
  }
  const usd = leg.usd;
  if (usd === null || !(usd > 0)) return { ok: false, why: 'сумма ноги не посчитана' };
  if (usd < MIN_EXCHANGE_ORDER_USD) {
    return { ok: false, why: `меньше минимального ордера: ${MIN_EXCHANGE_ORDER_USD} $` };
  }
  const shares = leg.shares ?? usd / price;
  if (leg.minSize !== null && shares < leg.minSize) {
    return { ok: false, why: `рынок не примет меньше ${leg.minSize} шаров` };
  }

  const otherOdds = Number.parseFloat(other.odds);
  const dead = deadPrice(price, otherOdds);
  if (dead === null) return { ok: false, why: 'кэф второй ноги не читается, потолка нет' };
  if (dead <= price) return { ok: false, why: 'вилки нет уже по показанной цене' };

  const tick = leg.tick ?? TICK;
  const room = Math.min(price * (1 + MAX_SLIP), price + (dead - price) * KEEP);
  let maxPrice = toTick(room, tick);
  // Округление вниз не имеет права увести потолок ниже уже показанной цены.
  // Биржа принимает только цену, кратную шагу. Показанные 53.4 ¢ при шаге в
  // цент — не цена ордера: FOK с потолком 0.534 не стыкуется со стаканом
  // 0.53/0.54 и умирает, хотя объём на месте. Поднимаем на один шаг, если этот
  // шаг ещё внутри запаса до смерти и внутри 3%.
  if (maxPrice < price) {
    const step = tick > 0 ? tick : TICK;
    const up = new Decimal(price).div(step).toDecimalPlaces(0, Decimal.ROUND_UP).times(step).toNumber();
    const slipCap = price * (1 + MAX_SLIP);
    maxPrice = up <= dead && up <= slipCap + 1e-12 ? up : price;
  }

  return { ok: true, plan: { tokenId: leg.asset, usd, price, maxPrice, dead, shares } };
}

/**
 * Сверка перед подписью: биржа назвала цену исполнения — берём или отказываемся.
 *
 * Это последняя точка, где отказ бесплатен. Дальше ордер подписан и отправлен, и
 * «передумать» означает продать обратно с потерей спреда.
 */
export function checkQuote(plan: OrderPlan, quoted: number): { ok: boolean; why: string } {
  if (!(quoted > 0)) return { ok: false, why: 'биржа не назвала цену' };
  if (quoted > plan.maxPrice) {
    return {
      ok: false,
      why: `цена ушла: ${centsOf(quoted)} против потолка ${centsOf(plan.maxPrice)}`,
    };
  }
  return { ok: true, why: `${centsOf(quoted)} при потолке ${centsOf(plan.maxPrice)}` };
}

/** Цена в центах для человека: он читает купон биржи в центах, а не в долях. */
export const centsOf = (price: number): string => `${new Decimal(price).times(100).toFixed(1)} ¢`;

/**
 * Ключ попытки: одна вилка, один токен, одна сумма — один ордер.
 *
 * Главный источник дублей — не сеть, а человек: нажал, вкладка перезагрузилась,
 * нажал ещё. Ключ обязан быть одинаковым у обоих нажатий и разным у двух разных
 * заходов в одну вилку, поэтому в него входит сумма, а не время.
 */
export const orderKey = (forkId: string, plan: OrderPlan): string =>
  `${forkId}:${plan.tokenId}:${plan.usd.toFixed(2)}`;
